import { Alert, Anchor, Badge, Button, Group, Loader, Paper, Stack, Table, Text, Title } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EmptyNote } from '../../components/RecordScreen';
import { formatQty } from '../../lib/format';
import { PlaceOrderForm } from '../suppliers/PlaceOrderForm';
import { REORDER_PATH } from './stock';
import type { LandedOffer } from './WhoSellsIt';
import { Crossing, crossingIsKnown, LandedCost, TheirPrice } from './WhoSellsIt';

interface ShortItem {
  item: {
    id: string;
    nameEn: string;
    category: string;
    stockUnit: string;
    parLevel?: number | null;
    reorderPoint?: number | null;
  };
  totalOnHand: number;
  suppliers: LandedOffer[];
}

/** How much it would take to get back to what the owner likes to hold. */
function shortfall(row: ShortItem): number | null {
  const target = row.item.parLevel ?? row.item.reorderPoint;
  if (typeof target !== 'number') return null;
  return Math.max(0, Math.round((target - row.totalOnHand) * 1000) / 1000);
}

interface VendorGroup {
  supplierId: string | null;
  supplierName: string;
  /** True when this vendor is here on goods prices alone — no shipping bill has been recorded. */
  shippingUnknown: boolean;
  rows: { row: ShortItem; offer: LandedOffer | undefined }[];
}

/**
 * The shortage list, arranged the way the orders actually go out: one vendor at a time.
 *
 * Every item is filed under whoever sells it cheapest once the crossing is paid — which is not
 * always whoever is cheapest per kilo, and is the comparison the owner was doing in his head. The
 * items nobody has quoted are kept together at the bottom rather than dropped, because "nobody has
 * a price for this" is a thing to fix, not a reason to hide a shortage.
 */
function groupByVendor(rows: ShortItem[]): VendorGroup[] {
  const groups = new Map<string, VendorGroup>();
  for (const row of rows) {
    const offer = row.suppliers[0];
    const key = offer?.supplierId ?? '';
    const existing = groups.get(key);
    const entry = { row, offer };
    if (existing) existing.rows.push(entry);
    else {
      groups.set(key, {
        supplierId: offer?.supplierId ?? null,
        supplierName: offer?.supplierName ?? 'Nobody has quoted a price',
        shippingUnknown: offer ? !crossingIsKnown(offer) : false,
        rows: [entry],
      });
    }
  }
  // Vendors first, in the order they came off the list; the unquoted pile last wherever it landed.
  return [...groups.values()].sort((a, b) => Number(a.supplierId === null) - Number(b.supplierId === null));
}

/**
 * What is running low, and who to ring about it.
 *
 * This list has always worked the shortfall out to three decimal places and then stopped, with a
 * description that told the reader to "work down this list when you put your next supplier order
 * together" — which meant writing it on paper, going to the supplier screen, starting an order, and
 * recalling from memory which vendor sells limes. Everything needed to skip all of that was in the
 * same database: the vendors' price lists are keyed by exactly these items.
 *
 * So the list is grouped by vendor and each group opens an order to that vendor, with their price
 * list as the pick list and a one-press fill for the items on this page. The comparison that
 * decides which vendor an item is filed under is landed cost — their price plus what the crossing
 * adds — because the mainland grower is usually cheaper on paper and often dearer in the fridge.
 */
export function RunningLowSection({ currency, locationId }: { currency: string; locationId?: string }) {
  const queryClient = useQueryClient();
  const [orderingFrom, setOrderingFrom] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: listKey(REORDER_PATH),
    queryFn: () => rawApi<ShortItem[]>('GET', REORDER_PATH),
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <Group gap="xs" py="xl" justify="center">
        <Loader size="sm" />
        <Text c="dimmed" size="sm">
          Loading…
        </Text>
      </Group>
    );
  }

  if (isError) {
    return (
      <Alert color="red" title="This could not be loaded">
        The running-low list could not be read just now. Nothing has changed. Refresh the page to try again.
      </Alert>
    );
  }

  const rows = data ?? [];
  const groups = groupByVendor(rows);

  return (
    <Stack gap="md">
      <div>
        <Title order={5}>Running low</Title>
        <Text size="xs" c="dimmed" mt={2}>
          Everything at or below the level you set for reordering, grouped by whoever sells it cheapest once
          the shipping is paid. Shipping comes from what the ferry and the freight forwarders have actually
          billed for that vendor's deliveries — a vendor nobody has recorded a shipping bill for is ranked on
          their goods price alone, and their rows say so.
        </Text>
      </div>

      {rows.length === 0 && <EmptyNote>Nothing needs reordering right now.</EmptyNote>}

      {groups.map((group) => (
        <Paper key={group.supplierId ?? 'unquoted'} withBorder radius="md" p="md">
          <Group justify="space-between" align="flex-start" mb="sm" wrap="nowrap">
            <div>
              {group.supplierId ? (
                <Anchor component={Link} to={`/suppliers/${group.supplierId}`} fw={600}>
                  {group.supplierName}
                </Anchor>
              ) : (
                <Text fw={600}>{group.supplierName}</Text>
              )}
              <Text size="xs" c="dimmed">
                {group.rows.length === 1 ? '1 item short' : `${group.rows.length} items short`}
              </Text>
            </div>
            <Group gap="xs" wrap="nowrap">
              {/* The button beside a vendor's name is the whole point of grouping this way, so a
                  group put together on goods prices alone has to say so before it is pressed. */}
              {group.shippingUnknown && (
                <Badge variant="light" color="orange">
                  Shipping not counted
                </Badge>
              )}
              {group.supplierId ? (
                <Button size="compact-sm" onClick={() => setOrderingFrom(group.supplierId)}>
                  Order these from them
                </Button>
              ) : (
                <Badge variant="light" color="yellow">
                  No price on file
                </Badge>
              )}
            </Group>
          </Group>

          <Table.ScrollContainer minWidth={760}>
            <Table verticalSpacing="xs" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Item</Table.Th>
                  <Table.Th>On hand</Table.Th>
                  <Table.Th>Order at least</Table.Th>
                  <Table.Th>Their price</Table.Th>
                  <Table.Th>Shipping</Table.Th>
                  <Table.Th>Costs you</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {group.rows.map(({ row, offer }) => {
                  const short = shortfall(row);
                  return (
                    <Table.Tr key={row.item.id}>
                      <Table.Td>
                        <Anchor component={Link} to={`/inventory/${row.item.id}`} fw={500} size="sm">
                          {row.item.nameEn}
                        </Anchor>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c={row.totalOnHand <= 0 ? 'red.7' : undefined}>
                          {/* Added up from every shelf on the API, so this is the one figure on
                              the page that arrives carrying binary noise: four shelves of a tenth
                              of a kilo is 3.4000000000000004. */}
                          {formatQty(row.totalOnHand, row.item.stockUnit)}
                        </Text>
                      </Table.Td>
                      {/* The one column this list exists for. Everything else here was already
                          knowable; "how much do I put on the order" was left as arithmetic
                          between two other columns. */}
                      <Table.Td>
                        <Text size="sm" fw={600}>
                          {short === null ? '—' : formatQty(short, row.item.stockUnit)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {offer ? (
                          <TheirPrice offer={offer} unit={row.item.stockUnit} />
                        ) : (
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        )}
                      </Table.Td>
                      {/* What the ferry adds, and where that came from. Without it "Costs you"
                          is a total whose second half the reader cannot see, on the screen that
                          decides which vendor gets the order. */}
                      <Table.Td>
                        {offer ? (
                          <Crossing offer={offer} currency={currency} unit={row.item.stockUnit} />
                        ) : (
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {offer ? (
                          <LandedCost offer={offer} currency={currency} />
                        ) : (
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      ))}

      {orderingFrom && (
        <PlaceOrderForm
          locationId={locationId}
          supplierId={orderingFrom}
          onClose={() => setOrderingFrom(null)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: listKey(REORDER_PATH) });
          }}
        />
      )}
    </Stack>
  );
}
